import { isValid, parseISO } from 'date-fns';

import { type Nullable } from '../../../api/types.ts';

type DateTimePartType = 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second';

export type TimeZoneDateParts = Record<DateTimePartType, string>;

const LOCAL_TIME_ZONE_KEY = '__local__';

const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>();
const monthFormatters = new Map<string, Intl.DateTimeFormat>();
const timeFormatters = new Map<string, Intl.DateTimeFormat>();

const getTimeZoneKey = (timeZone?: Nullable<string>) => timeZone || LOCAL_TIME_ZONE_KEY;

const getResolvedTimeZone = (timeZone?: Nullable<string>) => timeZone || undefined;

const getDateTimeFormatter = (timeZone?: Nullable<string>): Intl.DateTimeFormat => {
  const key = getTimeZoneKey(timeZone);
  const cached = dateTimeFormatters.get(key);

  if (cached) return cached;

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: getResolvedTimeZone(timeZone),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });

    dateTimeFormatters.set(key, formatter);
    return formatter;
  } catch {
    return getDateTimeFormatter();
  }
};

const getMonthFormatter = (timeZone: Nullable<string> | undefined, month: 'short' | 'long') => {
  const key = `${getTimeZoneKey(timeZone)}-${month}`;
  const cached = monthFormatters.get(key);

  if (cached) return cached;

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: getResolvedTimeZone(timeZone),
      month,
    });

    monthFormatters.set(key, formatter);
    return formatter;
  } catch {
    return getMonthFormatter(undefined, month);
  }
};

const getTimeFormatter = (timeZone?: Nullable<string>): Intl.DateTimeFormat => {
  const key = getTimeZoneKey(timeZone);
  const cached = timeFormatters.get(key);

  if (cached) return cached;

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: getResolvedTimeZone(timeZone),
      hour: 'numeric',
      minute: '2-digit',
    });

    timeFormatters.set(key, formatter);
    return formatter;
  } catch {
    return getTimeFormatter();
  }
};

export const parseDateValue = (date: Date | string): Nullable<Date> => {
  const parsed = typeof date === 'string' ? parseISO(date) : date;

  return isValid(parsed) ? parsed : null;
};

export const getTimeZoneDateParts = (
  date: Date | string,
  timeZone?: Nullable<string>
): Nullable<TimeZoneDateParts> => {
  const parsed = parseDateValue(date);

  if (!parsed) return null;

  const formattedParts = getDateTimeFormatter(timeZone).formatToParts(parsed);
  const parts = formattedParts.reduce<Partial<TimeZoneDateParts>>((acc, part) => {
    if (
      part.type === 'year' ||
      part.type === 'month' ||
      part.type === 'day' ||
      part.type === 'hour' ||
      part.type === 'minute' ||
      part.type === 'second'
    ) {
      acc[part.type] = part.value;
    }

    return acc;
  }, {});

  if (!parts.year || !parts.month || !parts.day || !parts.hour || !parts.minute || !parts.second) {
    return null;
  }

  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second,
  };
};

export const getTimeZoneMonthName = (
  date: Date | string,
  timeZone: Nullable<string> | undefined,
  month: 'short' | 'long'
) => {
  const parsed = parseDateValue(date);

  return parsed ? getMonthFormatter(timeZone, month).format(parsed) : null;
};

export const formatTimeInTimeZone = (date: Date | string, timeZone?: Nullable<string>) => {
  const parsed = parseDateValue(date);

  return parsed ? getTimeFormatter(timeZone).format(parsed) : null;
};

export type WallClockTime = {
  year: number;
  /** 1–12; out-of-range months and days roll over like Date.UTC. */
  month: number;
  day: number;
};

const getTimeZoneOffsetMs = (utcMs: number, timeZone?: Nullable<string>) => {
  const parts = getTimeZoneDateParts(new Date(utcMs), timeZone);

  if (!parts) return 0;

  const wallClockAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );

  return wallClockAsUtc - (utcMs - (utcMs % 1000));
};

export const zonedStartOfDayToUtc = (wallClock: WallClockTime, timeZone?: Nullable<string>) => {
  const wallClockAsUtc = Date.UTC(wallClock.year, wallClock.month - 1, wallClock.day);
  const firstGuess = wallClockAsUtc - getTimeZoneOffsetMs(wallClockAsUtc, timeZone);
  // A second pass picks up a DST change between the guess and the real instant.
  return new Date(wallClockAsUtc - getTimeZoneOffsetMs(firstGuess, timeZone));
};

export const getZonedDate = (date: Date | string, timeZone?: Nullable<string>): Nullable<Date> => {
  const parts = getTimeZoneDateParts(date, timeZone);

  if (!parts) return null;

  return new Date(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
};
